/**
 * PhoneLogic - Service functions for Phone operations
 */

import type { Phone, PhoneType, PhoneTypeKind } from './types';
import { generateUUID } from '@/util/uuid';
import { validatePhone as validatePhoneSchema } from './schema';

export const PhoneLogic = {
  /**
   * Singleton instances for known phone types.
   */
  PHONE_TYPES: {
    cell: { kind: 'cell', label: 'Mobile' },
    home: { kind: 'home', label: 'Home' },
    phone: { kind: 'phone', label: 'Phone' },
    work: { kind: 'work', label: 'Work' },
  } as const satisfies Record<string, PhoneType>,

  /**
   * All phone type kinds for dropdown iteration.
   */
  PHONE_TYPE_KINDS: ['cell', 'home', 'phone', 'work', 'other'] as const,

  /**
   * Create an 'other' PhoneType with the given raw string.
   */
  makeOther(raw: string): PhoneType {
    return { kind: 'other', label: 'Other', raw } as const;
  },

  /**
   * Map a vCard TYPE string to a PhoneType.
   * Unknown values become { kind: 'other', raw: originalString }.
   */
  fromVCardTypeString(typeStr: string): PhoneType {
    const lower = typeStr.toLowerCase();
    if (lower === 'mobile' || lower === 'cell') return this.PHONE_TYPES.cell;
    if (lower === 'home') return this.PHONE_TYPES.home;
    if (lower === 'work') return this.PHONE_TYPES.work;
    if (lower === 'phone') return this.PHONE_TYPES.phone;
    return this.makeOther(typeStr);
  },

  /**
   * Convert a PhoneType to the string used in vCard TYPE= field.
   * 'other' kind writes the raw string directly.
   */
  toVCardTypeString(type: PhoneType): string {
    if (type.kind === 'other') return type.raw;
    return type.kind;
  },

  /**
   * Get the kind string from a PhoneType (or 'other' if undefined).
   */
  getKind(type: PhoneType | undefined): PhoneTypeKind {
    return type?.kind ?? 'other';
  },

  /**
   * Create a validated Phone from input string. Throws if invalid.
   */
  create(input: string): Phone {
    if (!input || input.trim() === '') {
      throw new Error('Phone number cannot be empty');
    }

    let cleanedNumber = input.replace(/\D/g, '');

    if (cleanedNumber.length === 11 && cleanedNumber[0] === '1') {
      cleanedNumber = cleanedNumber.substring(1);
    }

    if (cleanedNumber.length !== 10) {
      throw new Error(
        `Phone number must be exactly 10 digits (got ${cleanedNumber.length})`
      );
    }

    return { value: cleanedNumber, localId: generateUUID() };
  },

  /**
   * Try to create a Phone, returning null if invalid.
   */
  tryCreate(input: string): Phone | null {
    try {
      return this.create(input);
    } catch {
      return null;
    }
  },

  /**
   * Format phone value as (XXX) XXX-XXXX
   */
  format(value: string): string {
    if (value.length !== 10) {
      return value;
    }
    return `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
  },

  /**
   * Legacy: same as format (prefer format).
   */
  formattedLegacy(value: string): string {
    return this.format(value);
  },

  /**
   * Normalize phone string to 10 digits (removes all non-digits).
   */
  normalize(phone: string): string {
    return phone.replace(/\D/g, '').slice(-10);
  },

  /**
   * Normalize phone-like search input by removing all non-digits.
   */
  normalizeForSearch(phone: string): string {
    return phone.replace(/\D/g, '');
  },

  /**
   * True when the given string looks like the whole or part of a phone number
   */
  isPartialPhone(input: string): boolean {
    const trimmed = input.trim();
    return /\d/.test(trimmed) && /^[\d\s+().-]+$/.test(trimmed);
  },

  /**
   * True when the given string looks like the whole or part of a U.S.
   * phone number
   */
  isPartialUSPhone(input: string): boolean {
    const trimmed = input.trim();
    if (!this.isPartialPhone(trimmed)) {
      return false;
    }
    return !trimmed.startsWith('+') || trimmed.startsWith('+1');
  },

  /**
   * True when a phone value contains the phone-like query digits.
   */
  matchesPhoneSearch(phone: string, query: string): boolean {
    const queryDigits = this.normalizeForSearch(query);
    if (!queryDigits) {
      return false;
    }
    return this.normalizeForSearch(phone).includes(queryDigits);
  },

  /**
   * Alias for normalize (for merge/comparison logic).
   */
  normalizeForComparison(phone: string): string {
    return this.normalize(phone);
  },

  /**
   * Return last 2 digits of phone value.
   */
  tail(phone: Phone): string {
    return phone.value.slice(-2);
  },

  /**
   * Get display label for phone type.
   */
  getPhoneTypeLabel(type: PhoneType | undefined): string {
    if (!type) return 'Other';
    return type.label;
  },

  /**
   * Check if a phone has a valid US number (10 digits, or 11 starting with 1).
   */
  isValidUS(phone: Phone): boolean {
    const digits = phone.value.replace(/\D/g, '');
    return digits.length === 10 || (digits.length === 11 && digits[0] === '1');
  },

  /**
   * Check if two Phones have the same value.
   */
  equals(a: Phone, b: Phone): boolean {
    return a.value === b.value;
  },

  /**
   * Numeric sort key for phone type ordering.
   */
  typeOrder(type: PhoneType | undefined): number {
    const kind = this.getKind(type);
    const order: Record<PhoneTypeKind, number> = {
      cell: 0,
      work: 1,
      home: 2,
      phone: 3,
      other: 4,
    };
    return order[kind];
  },

  /**
   * Compare two phones for merging (returns -1, 0, or 1).
   */
  compare(tel1: Phone, tel2: Phone): number {
    const kind1 = this.getKind(tel1.type);
    const kind2 = this.getKind(tel2.type);
    const hasLabel1 = kind1 !== 'other' || tel1.pref !== undefined;
    const hasLabel2 = kind2 !== 'other' || tel2.pref !== undefined;
    if (hasLabel1 && !hasLabel2) return -1;
    if (!hasLabel1 && hasLabel2) return 1;

    const typeDiff = this.typeOrder(tel1.type) - this.typeOrder(tel2.type);
    if (typeDiff !== 0) return typeDiff;

    const pref1 = tel1.pref ?? Number.MAX_SAFE_INTEGER;
    const pref2 = tel2.pref ?? Number.MAX_SAFE_INTEGER;
    return pref1 - pref2;
  },

  /**
   * Merge multiple phone arrays following preference rules.
   */
  merge(allTels: Phone[][]): Phone[] {
    const phoneMap = new Map<string, Phone>();

    for (const tels of allTels) {
      for (const tel of tels) {
        const normalized = this.normalizeForComparison(tel.value);
        const existing = phoneMap.get(normalized);

        if (!existing) {
          phoneMap.set(normalized, tel);
        } else {
          if (this.compare(tel, existing) < 0) {
            phoneMap.set(normalized, tel);
          }
        }
      }
    }

    return Array.from(phoneMap.values());
  },

  /**
   * Format phone for display in input field: (XXX) XXX-XXXX.
   */
  formatForInput(phone: string): string {
    const normalized = this.normalize(phone);
    if (normalized.length !== 10) {
      return phone;
    }
    return `(${normalized.slice(0, 3)}) ${normalized.slice(3, 6)}-${normalized.slice(6)}`;
  },

  /**
   * Validate a Phone object. Returns null if invalid.
   */
  validate(data: unknown): Phone | null {
    return validatePhoneSchema(data);
  },
};
